import axios from 'axios';
import { randomUuid } from '@/practice/crypto';
import { domainPort, protocol } from '@/url';
import { areClientStorageWritesBlocked } from '@/practice/authSession';

export type ProductFunnelStage =
  | 'first_visit'
  | 'standard_trial_onboarding_assigned'
  | 'onboarding_start'
  | 'onboarding_page'
  | 'account_linked'
  | 'onboarding_completed'
  | 'trial_call_completed'
  | 'paywall_view'
  | 'checkout_clicked'
  | 'purchase_completed';

export type ProductFunnelOnboardingFlow = 'default' | 'long';

export type ProductFunnelOnboardingPage =
  | 'initial'
  | 'name'
  | 'native-language'
  | 'target-language'
  | 'proficiency'
  | 'age-group'
  | 'daily-goal'
  | 'marketing-source'
  | 'final-selling'
  | 'select-agent'
  | 'curriculum'
  | 'long-goals'
  | 'long-study-modes'
  | 'long-plan'
  | 'long-review'
  | 'long-interests'
  | 'long-more-interests'
  | 'long-tutor-language'
  | 'long-corrections'
  | 'long-generation'
  | 'long-curriculum-preview';

type ProductFunnelDimensions = {
  onboarding_flow?: ProductFunnelOnboardingFlow;
  source_lang?: string;
  target_lang?: string;
};

export type ProductFunnelEvent = ProductFunnelDimensions & (
  | {
    stage: 'onboarding_page';
    onboarding_page: ProductFunnelOnboardingPage;
  }
  | {
    stage: Exclude<ProductFunnelStage, 'onboarding_page'>;
    onboarding_page?: never;
  }
);

type ProductFunnelPayload = ProductFunnelEvent & {
  installation_id: string;
  occurred_at_ms: number;
};

export type TrialCallCompletionContext = {
  isForChat: boolean;
  isIntroLesson: boolean;
  sessionId: string | null;
  userStatus: string | undefined;
  trialEnded: boolean;
};

const installationIdStorageKey = 'issen_product_funnel_installation_id';
const recordedEventStoragePrefix = 'issen_product_funnel_recorded:';
const firstVisitRetryDelaysMs = [0, 2000, 10000] as const;
const pendingEventKeys = new Set<string>();
const recordedEventKeys = new Set<string>();
let memoryInstallationId: string | null = null;

export function shouldRecordTrialCallCompleted(
  context: TrialCallCompletionContext,
): boolean {
  return (
    context.isForChat
    && context.isIntroLesson
    && context.sessionId !== null
    && context.userStatus !== undefined
    && (context.userStatus === 'TRIAL' || context.trialEnded)
  );
}

export function getProductFunnelInstallationId(): string {
  if (areClientStorageWritesBlocked()) {
    memoryInstallationId ??= randomUuid();
    return memoryInstallationId;
  }
  try {
    const storedInstallationId = localStorage.getItem(installationIdStorageKey);
    if (storedInstallationId !== null && storedInstallationId.length > 0) {
      return storedInstallationId;
    }
    const installationId = randomUuid();
    localStorage.setItem(installationIdStorageKey, installationId);
    return installationId;
  } catch {
    memoryInstallationId ??= randomUuid();
    return memoryInstallationId;
  }
}

function getEventKey(event: ProductFunnelEvent): string {
  return [event.stage, event.onboarding_page ?? ''].join(':');
}

export async function recordProductFunnelEvent(event: ProductFunnelEvent): Promise<boolean> {
  const eventKey = getEventKey(event);
  const storageKey = `${recordedEventStoragePrefix}${eventKey}`;
  if (pendingEventKeys.has(eventKey)) {
    return false;
  }
  try {
    if (recordedEventKeys.has(eventKey) || localStorage.getItem(storageKey) === 'true') {
      return true;
    }
  } catch {
    if (recordedEventKeys.has(eventKey)) {
      return true;
    }
  }

  pendingEventKeys.add(eventKey);
  const payload: ProductFunnelPayload = {
    installation_id: getProductFunnelInstallationId(),
    occurred_at_ms: Date.now(),
    ...event,
  };

  try {
    await axios.post(`${protocol}${domainPort}/record_product_funnel_event`, payload, {
      timeout: 1500,
      headers: {
        'Client-Platform': 'web',
        'Client-Version': '1.16.19',
        'Content-Type': 'application/json',
      },
    });
    try {
      if (areClientStorageWritesBlocked() === false) {
        localStorage.setItem(storageKey, 'true');
      }
    } catch {
      recordedEventKeys.add(eventKey);
    }
    return true;
  } catch {
    return false;
  } finally {
    pendingEventKeys.delete(eventKey);
  }
}

export async function recordProductFunnelFirstVisit(): Promise<void> {
  for (const retryDelayMs of firstVisitRetryDelaysMs) {
    if (retryDelayMs > 0) {
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, retryDelayMs);
      });
    }
    if (await recordProductFunnelEvent({ stage: 'first_visit' })) {
      return;
    }
  }
}
